import React, { useState, useEffect } from 'react';
import AdminLayout from '../../../components/AdminLayout';
import { StatsCard, LoadingState } from '../../../components/admin/shared';
import { Users, TrendingUp, UserPlus, Globe } from 'lucide-react';
import educentialApi from '../../../api/educentialApi';

const UserAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    loadUserAnalytics();
  }, [days]);

  const loadUserAnalytics = async () => {
    setLoading(true);
    try {
      const response = await educentialApi.get(`/api/admin/reports/users?days=${days}`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error loading user analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="User Analytics">
        <LoadingState message="Loading user analytics..." />
      </AdminLayout>
    );
  }

  const { growthData, demographics } = analytics || {};

  // Calculate totals
  const latestData = growthData && growthData.length > 0 ? growthData[growthData.length - 1] : null;
  const totalNewUsers = growthData?.reduce((sum, day) => sum + day.newUsers, 0) || 0;
  const avgNewUsersPerDay = growthData?.length > 0 ? (totalNewUsers / growthData.length).toFixed(1) : 0;

  return (
    <AdminLayout title="User Analytics">
      <div className="p-6 space-y-6">
        {/* Header with Time Range Selector */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">User Analytics</h2>
            <p className="text-gray-600 mt-1">Growth trends and user demographics</p>
          </div>
          <div className="flex items-center space-x-2">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  days === d
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {d} Days
              </button>
            ))}
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatsCard
            title="Total Users"
            value={latestData?.totalUsers || 0}
            icon={Users}
          />
          <StatsCard
            title={`New Users (${days}d)`}
            value={totalNewUsers}
            icon={UserPlus}
          />
          <StatsCard
            title="Avg/Day"
            value={avgNewUsersPerDay}
            icon={TrendingUp}
          />
          <StatsCard
            title="Countries"
            value={Object.keys(demographics?.byCountry || {}).length}
            icon={Globe}
          />
        </div>

        {/* Growth Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">User Growth Trend</h3>
          {growthData && growthData.length > 0 ? (
            <div className="space-y-4">
              {/* Simple bar chart */}
              <div className="relative h-64">
                <div className="absolute inset-0 flex items-end justify-between space-x-1">
                  {growthData.slice(-days).map((day, index) => {
                    const maxUsers = Math.max(...growthData.map(d => d.newUsers));
                    const height = maxUsers > 0 ? (day.newUsers / maxUsers) * 100 : 0;

                    return (
                      <div key={index} className="flex-1 flex flex-col items-center group">
                        <div
                          className="w-full bg-blue-500 hover:bg-blue-600 transition-colors rounded-t cursor-pointer relative"
                          style={{ height: `${height}%` }}
                          title={`${new Date(day.date).toLocaleDateString()}: ${day.newUsers} new users`}
                        >
                          <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                            {day.newUsers} users
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* X-axis labels */}
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>{growthData[0]?.date ? new Date(growthData[0].date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</span>
                <span>{latestData?.date ? new Date(latestData.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</span>
              </div>

              {/* Legend */}
              <div className="flex items-center justify-center space-x-6 pt-4 border-t">
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-blue-500 rounded"></div>
                  <span className="text-sm text-gray-600">New Users Per Day</span>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No growth data available</p>
          )}
        </div>

        {/* Demographics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* By Role */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Users by Role</h3>
            <div className="space-y-3">
              {demographics?.byRole && Object.entries(demographics.byRole).map(([role, count]) => {
                const total = Object.values(demographics.byRole).reduce((a, b) => a + b, 0);
                const percentage = ((count / total) * 100).toFixed(1);

                const colors = {
                  STUDENT: 'bg-green-500',
                  EDUCATOR: 'bg-blue-500',
                  ADMIN: 'bg-purple-500'
                };

                return (
                  <div key={role}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">{role}</span>
                      <span className="text-sm text-gray-600">
                        {count} ({percentage}%)
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${colors[role] || 'bg-gray-500'}`}
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* By Country (Top 10) */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Countries</h3>
            {demographics?.byCountry && Object.keys(demographics.byCountry).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(demographics.byCountry)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 10)
                  .map(([country, count]) => {
                    const total = Object.values(demographics.byCountry).reduce((a, b) => a + b, 0);
                    const percentage = ((count / total) * 100).toFixed(1);

                    return (
                      <div key={country}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-700">{country}</span>
                          <span className="text-sm text-gray-600">
                            {count} ({percentage}%)
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="h-2 rounded-full bg-blue-500"
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No country data available</p>
            )}
          </div>
        </div>

        {/* Insights */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Insights</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm font-medium text-blue-900">Growth Rate</p>
              <p className="text-2xl font-bold text-blue-600 mt-2">
                {growthData && growthData.length > 1
                  ? ((totalNewUsers / days) * 30).toFixed(0)
                  : 0}
              </p>
              <p className="text-xs text-blue-700 mt-1">Projected monthly growth</p>
            </div>

            <div className="p-4 bg-green-50 rounded-lg">
              <p className="text-sm font-medium text-green-900">Most Popular Role</p>
              <p className="text-2xl font-bold text-green-600 mt-2">
                {demographics?.byRole
                  ? Object.entries(demographics.byRole).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A'
                  : 'N/A'}
              </p>
              <p className="text-xs text-green-700 mt-1">
                {demographics?.byRole
                  ? `${Object.entries(demographics.byRole).sort((a, b) => b[1] - a[1])[0]?.[1] || 0} users`
                  : ''}
              </p>
            </div>

            <div className="p-4 bg-purple-50 rounded-lg">
              <p className="text-sm font-medium text-purple-900">Top Country</p>
              <p className="text-2xl font-bold text-purple-600 mt-2">
                {demographics?.byCountry
                  ? Object.entries(demographics.byCountry).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A'
                  : 'N/A'}
              </p>
              <p className="text-xs text-purple-700 mt-1">
                {demographics?.byCountry
                  ? `${Object.entries(demographics.byCountry).sort((a, b) => b[1] - a[1])[0]?.[1] || 0} users`
                  : ''}
              </p>
            </div>

            <div className="p-4 bg-yellow-50 rounded-lg">
              <p className="text-sm font-medium text-yellow-900">Busiest Day</p>
              <p className="text-2xl font-bold text-yellow-600 mt-2">
                {growthData && growthData.length > 0
                  ? new Date(
                      growthData.reduce((max, day) =>
                        day.newUsers > max.newUsers ? day : max
                      ).date
                    ).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                  : 'N/A'}
              </p>
              <p className="text-xs text-yellow-700 mt-1">
                {growthData && growthData.length > 0
                  ? `${Math.max(...growthData.map(d => d.newUsers))} new users`
                  : ''}
              </p>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default UserAnalytics;
